import { Card, PageHeader } from '@ksyos/design-system';
import { useNavigate, useSearchParams } from '@ksyos/router';
import { useEffect, useState } from 'react';
import type { IReprocessPaymentsFilter } from '@common/utils/reprocessPaymentsFilter';
import type { IPaymentsFilter } from '@common/validation/payouts';
import type { IProcessActivityWithChecked } from '@frontend/components/interfaces';
import type { IPaymentsSearchFilters } from '@frontend/components/interfaces/paymentsSearchFilters';
import { ExportStatusFilter, ManagerAuthorization } from '@common/enums';
import { normalizeSearchText } from '@common/utils/normalizeSearchText';
import { stringify } from '@common/utils/queryStringUtils';
import { searchTextMatchesProcessId } from '@common/utils/searchTextMatchesProcessId';
import { tryParseInt } from '@common/utils/tryParseInt';
import { UrlBuilder } from '@common/utils/urlBuilder';
import { paymentsSearchFilterValidation, paymentsSearchFilterValidationSchema } from '@common/validation/payments';
import { Loader } from '@frontend/components/elements/common';
import { DisplayMessage } from '@frontend/components/elements/DisplayMessage';
import { ListPager } from '@frontend/components/elements/ListPager';
import { ConfirmationModal } from '@frontend/components/elements/modals/ConfirmationModal';
import { FinancialLogMutationModal } from '@frontend/components/elements/modals/FinancialLogMutationModal';
import { PageTemplate } from '@frontend/components/templates/PageTemplate';
import { getSelectedLanguage } from '@frontend/translate';
import { assertHasAuthorization, useAbortableFetch } from '@frontend/utils';
import { formatDateIntoString } from '@frontend/utils/formatDateIntoString';
import { validateLocationQuery } from '../financeHelperFunctions';
import { NoFinancingSearchResults } from '../NoFinancingSearchResults';
import { PaymentsFooterButtons } from './PaymentsFooterButtons';
import { isUsedForReprocessing } from './PaymentsSearchFilters/paymentsHelperFunctions';
import { PaymentsSearchFilters } from './PaymentsSearchFilters/PaymentsSearchFilters';
import { PaymentsTable } from './PaymentsTable/PaymentsTable';
import { ReprocessAction } from './reprocessAction';

export function Payments() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const queryPage = tryParseInt(searchParams.getOptional('page'), 1);
    const language = getSelectedLanguage();
    const itemsPerPage = 10;
    const abortableFetch = useAbortableFetch();

    const [isReprocessing, setIsReprocessing] = useState(false);
    const [isSearching, setIsSearching] = useState(false);
    const [total, setTotal] = useState(0);
    const [payments, setPayments] = useState<IProcessActivityWithChecked[]>([]);
    const [reprocessedActivities, setReprocessedActivities] = useState<string[]>([]);
    const [message, setMessage] = useState<string>();
    const [allPaymentItemsSelected, setAllPaymentItemsSelected] = useState(false);
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [activitiesToReprocess, setActivitiesToReprocess] = useState(0);
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    const [reprocessAction, setReprocessAction] = useState<ReprocessAction | undefined>();
    const [paymentsSearchFilters, setPaymentsSearchFilters] = useState<IPaymentsSearchFilters>({
        exportStatus: ExportStatusFilter.both,
        payoutsOnly: false,
    });

    const {
        activityType,
        endDate,
        episodeCategoryId,
        exportStatus,
        groupId,
        noPayoutReason,
        payoutsOnly,
        performerOrganizationId,
        performerPractitionerId,
        searchField,
        searchText,
        serviceId,
        startDate,
    } = paymentsSearchFilters;

    assertHasAuthorization(ManagerAuthorization.financePayoutsRead);

    useEffect(() => {
        const queryParameters = validateLocationQuery(searchParams, paymentsSearchFilterValidationSchema.schema);

        setPaymentsSearchFilters({ ...paymentsSearchFilters, ...queryParameters });
    }, []);

    return (
        <PageTemplate>
            <PageHeader title={language.finance.payments} />
            <PaymentsSearchFilters
                isSearchButtonDisabled={getIsSearchButtonDisabled()}
                onSearch={() => handleSearch()}
                onSetPaymentsSearchFilters={setPaymentsSearchFilters}
                paymentsSearchFilters={paymentsSearchFilters}
            />
            {message ? (
                <DisplayMessage
                    message={message}
                    messageType="info"
                />
            ) : null}
            {payments.length > 0 && (
                <>
                    <Card>
                        <Card.Header title={getSelectedLanguage().common.searchResults} />
                        <PaymentsTable
                            allPaymentItemsSelected={allPaymentItemsSelected}
                            items={payments}
                            onSelectAllPaymentItems={handleSelectAllPaymentItems}
                            onSelectPaymentItem={handleSelectPaymentItem}
                            reprocessedActivities={reprocessedActivities}
                        />
                        <Card.Footer>
                            <ListPager
                                isDisabled={isSearching}
                                itemsPerPage={itemsPerPage}
                                onChangePage={handleNavigation}
                                page={queryPage}
                                total={total}
                            />
                        </Card.Footer>
                    </Card>
                    <PaymentsFooterButtons
                        isDisabled={isSearching || payments.filter((payment) => payment.checked).length === 0}
                        isReportDisabled={isSearching || isReprocessing}
                        onProcessActivity={(value) => {
                            handleProcessActivity(value);
                        }}
                        onReport={async () => {
                            await generateReport();
                        }}
                    />
                </>
            )}
            {isSearching && <Loader fixedCentered />}
            <NoFinancingSearchResults
                isInitialLoad={isInitialLoad}
                numberOfItems={payments.length}
            />
            {showConfirmation && renderConfirmationModal()}
        </PageTemplate>
    );

    async function handleSearch() {
        setMessage(undefined);
        setIsSearching(true);
        setIsInitialLoad(false);
        pushBrowserHistory(1);
        await search();
    }

    async function search(page?: string) {
        if (!isAtLeastOneFilterSelected()) {
            setIsSearching(false);

            return;
        }

        if (isWrongSearch()) {
            setTotal(0);
            setPayments([]);
        } else {
            const data = {
                filter: {
                    ...getFilters(),
                    paging: {
                        limit: itemsPerPage,
                        page: page ?? '1',
                    },
                },
            };

            const result = await abortableFetch.fetchJson('/api/payments', 'POST', data);
            const items: IProcessActivityWithChecked[] = result.json.items;
            for (const item of items) {
                item.checked = allPaymentItemsSelected;
            }
            setTotal(result.json.total);
            setPayments(items);
            setReprocessedActivities([]);
        }

        setIsSearching(false);
    }

    function handleSelectPaymentItem(value: boolean, index: number) {
        const newPayments = [...payments];
        newPayments[index].checked = value;

        setPayments(newPayments);
        setAllPaymentItemsSelected(allPaymentItemsSelected && value);
    }

    function handleSelectAllPaymentItems(value: boolean) {
        const newPayments = [...payments].map((payment) => ({
            ...payment,
            checked: isUsedForReprocessing(payment, payments) && value,
        }));

        setPayments(newPayments);
        setAllPaymentItemsSelected(value);
    }

    async function handleNavigation(page: number) {
        setIsSearching(true);
        pushBrowserHistory(page);
        await search(page.toString());
    }

    function pushBrowserHistory(page?: number) {
        const queryParameters = Object.keys(paymentsSearchFilterValidation).reduce((map: any, key) => {
            const value = paymentsSearchFilters[key as keyof typeof paymentsSearchFilters];
            if (value !== undefined && value !== null && value !== '') {
                if (key === 'startDate' || key === 'endDate') {
                    if (paymentsSearchFilters[key]) {
                        map[key] = formatDateIntoString(paymentsSearchFilters[key]);
                    }
                } else {
                    map[key] = value;
                }
            }

            return map;
        }, {});

        if (page) {
            queryParameters.page = page;
        }

        navigate(`/finance/payments?${stringify({ ...queryParameters })}`);
    }

    function handleProcessActivity(value: ReprocessAction) {
        setReprocessAction(value);

        setActivitiesToReprocess(
            allPaymentItemsSelected ? total : payments.filter((payment) => payment.checked).length
        );
        setShowConfirmation(true);
    }

    async function onReprocessPayouts(reprocessReason?: string) {
        setIsReprocessing(true);
        setShowConfirmation(false);
        let filter: IReprocessPaymentsFilter;

        if (allPaymentItemsSelected) {
            filter = { paymentsFilter: getFilters() };
        } else {
            const selectedActivities = payments
                .filter((payment) => payment.checked)
                .map((payment) => ({ id: payment.id, payoutType: payment.activityType, processId: payment.processId }));
            filter = { requestIds: selectedActivities };
        }

        filter.setIsUnpaidActivity =
            reprocessAction === ReprocessAction.paid
                ? false
                : reprocessAction === ReprocessAction.unpaid
                  ? true
                  : undefined;

        setMessage(language.finance.reprocessingPayouts);
        const result = await abortableFetch.fetchJson('/api/payments/reprocess', 'POST', { filter, reprocessReason });
        setReprocessedActivities([...reprocessedActivities, ...result.json.reprocessedActivities]);
        setReprocessAction(undefined);

        if (result.json.reprocessedActivities.length > 0) {
            setIsSearching(true);
            pushBrowserHistory(1);
            await search();
            handleSelectAllPaymentItems(false);
            setIsReprocessing(false);
        }
    }

    function getFilters() {
        const filter: IPaymentsFilter = {
            activityType,
            endDate: formatDateIntoString(endDate),
            episodeCategoryId,
            exportStatus,
            groupId,
            noPayoutReason,
            payoutsOnly,
            performerOrganizationId,
            performerPractitionerId,
            serviceId,
            startDate: formatDateIntoString(startDate),
        };

        if (searchText) {
            const trimmedSearchText = normalizeSearchText(searchText);
            switch (searchField) {
                case 'processId':
                    if (searchTextMatchesProcessId(trimmedSearchText)) {
                        filter.processId = trimmedSearchText;
                    }
                    break;
                case 'txcsProcesId':
                    filter.txcsProcesId = trimmedSearchText;
                    break;
                case 'practitioner':
                    filter.practitionerIdOrUid = trimmedSearchText;
                    break;
                case 'organization':
                    filter.organizationId = trimmedSearchText;
                    break;
                case 'relationNumber':
                    filter.relationNumber = trimmedSearchText;
                    break;
                default:
                    filter.searchText = trimmedSearchText;
            }
        }

        return filter;
    }

    function isAtLeastOneFilterSelected() {
        return (
            searchText ||
            startDate ||
            endDate ||
            serviceId ||
            episodeCategoryId ||
            groupId ||
            payoutsOnly ||
            noPayoutReason ||
            performerPractitionerId ||
            performerOrganizationId ||
            exportStatus !== ExportStatusFilter.both ||
            activityType
        );
    }

    function getIsSearchButtonDisabled() {
        return (
            isSearching ||
            (!searchText &&
                !startDate &&
                !endDate &&
                !serviceId &&
                !episodeCategoryId &&
                !groupId &&
                !payoutsOnly &&
                !noPayoutReason &&
                !performerOrganizationId &&
                !performerPractitionerId &&
                !activityType)
        );
    }

    function isWrongSearch() {
        let wrongSearch = false;
        if (searchText) {
            const trimmedSearchText = normalizeSearchText(searchText);
            if (searchField === 'processId' && !searchTextMatchesProcessId(trimmedSearchText)) {
                wrongSearch = true;
            }
        }

        return wrongSearch;
    }

    async function generateReport() {
        if (isWrongSearch()) {
            setTotal(0);
            setPayments([]);
        } else {
            const filters = getFilters();
            const urlBuilder = new UrlBuilder('/api/payments/report');
            for (const [key, value] of Object.entries(filters)) {
                if (value !== null && value !== undefined) {
                    urlBuilder.add(key, value);
                }
            }
            window.open(urlBuilder.build());
        }
    }

    function renderConfirmationModal() {
        if (reprocessAction === ReprocessAction.unpaid) {
            return (
                <FinancialLogMutationModal
                    handleNo={async () => setShowConfirmation(false)}
                    handleYes={async (reason) => await onReprocessPayouts(reason)}
                    isOpen
                />
            );
        } else {
            return (
                <ConfirmationModal
                    handleNo={() => setShowConfirmation(false)}
                    handleYes={async () => await onReprocessPayouts()}
                    isOpen
                    question={`Weet je zeker dat je ${activitiesToReprocess} ${
                        activitiesToReprocess === 1 ? 'uitbetaling' : 'uitbetalingen'
                    } opnieuw wilt verwerken?`}
                    title={language.finance.reprocessPayout}
                />
            );
        }
    }
}
